import React, { useState, useEffect } from 'react';
import { Layout, Menu, theme, Typography, Space, Button, Card, Row, Col, Table, DatePicker, Select, Tag, Modal, Form, Input, message, Statistic, Divider, Tooltip, Badge, Dropdown } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import UserProfile from './UserProfile';
import DashboardSection from './DashboardSection';
import { reportingAPI, beneficiaryAPI } from '../services/api';
import {
  DashboardOutlined,
  UserOutlined,
  StarOutlined,
  RiseOutlined,
  SettingOutlined,
  CalendarOutlined,
  CrownOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  BankOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  PrinterOutlined,
  ReloadOutlined,
  CalculatorOutlined,
  ReconciliationOutlined,
  DownOutlined,
  MailOutlined,
  TrophyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import './Reporting.css';
import '../styles/sidebar-standard.css';
import '../styles/menu-hover-overrides.css';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface PayoutData {
  key: string;
  beneficiaryId: number;
  beneficiaryName: string;
  totalDonations: number;
  monthlyDonations: number;
  oneTimeDonations: number;
  donationCount: number;
  ccProcessingFees: number; // what Stripe ACTUALLY charged, backfilled from Stripe
  /**
   * Collected for fees minus what Stripe actually took.
   *
   * The app grosses the donor's total up using Stripe's nonprofit rate
   * (2.2% + $0.30) and rounds UP to the cent, so a $15 gift is charged as
   * $15.00 + $3.00 platform + $0.72 = $18.72. Stripe's real fee is usually a
   * little less — $0.66 on that charge — and the difference lands in the
   * beneficiary's payout. Small per donation, real across a year, and it was
   * invisible before this column.
   */
  feeSurplus: number;
  netAmount: number; // Total - Platform Fee - CC Fees
  platformFee: number; // $3 per donation (THRIVE's revenue)
  payoutAmount: number; // Total donations - platform fee - absorbed processing fees
  stripeAmount: number; // Actual amount collected from Stripe
  reconciliationStatus: 'matched' | 'needs_review' | 'pending';
  bankInfo: {
    hasBankInfo: boolean;
    bankName?: string;
    accountHolderName?: string;
    routingNumber?: string;
    accountNumber?: string;
    paymentMethod: 'direct_deposit' | 'check';
  };
  payoutStatus: 'pending' | 'processing' | 'completed' | 'cancelled';
  payoutDate?: string;
  notes?: string;
}

const Reporting: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [payoutData, setPayoutData] = useState<PayoutData[]>([]);
  // Highlights section: independent period selector (Month / Quarter / Year).
  // Beneficiary Payouts section: M / Q toggle for the payouts table below.
  const [highlightsPeriod, setHighlightsPeriod] = useState<
    'monthly' | 'quarterly' | 'yearly'
  >('monthly');
  const [highlightsDate, setHighlightsDate] = useState<Dayjs>(dayjs());
  const [highlightsTotals, setHighlightsTotals] = useState<{
    totalDonations: number;
    totalCCFees: number;
    totalPlatformFee: number;
    totalPayoutAmount: number;
    totalDonationCount: number;
  } | null>(null);
  const [payoutsPeriod, setPayoutsPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>(
    'monthly',
  );
  const [loading, setLoading] = useState(false);
  const [bankInfoModalVisible, setBankInfoModalVisible] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<PayoutData | null>(null);
  const [payoutStatusModalVisible, setPayoutStatusModalVisible] = useState(false);
  const [needsReviewFilter, setNeedsReviewFilter] = useState(false);
  const [bankInfoFilter, setBankInfoFilter] = useState<'all' | 'has' | 'missing'>('all');
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();


  // Compute the [start, end] YYYY-MM-DD pair for a given period + anchor date.
  const rangeFor = (
    period: 'monthly' | 'quarterly' | 'yearly',
    anchor: Dayjs,
  ): [string, string] => {
    switch (period) {
      case 'quarterly':
        return [
          anchor.startOf('quarter').format('YYYY-MM-DD'),
          anchor.endOf('quarter').format('YYYY-MM-DD'),
        ];
      case 'yearly':
        return [
          anchor.startOf('year').format('YYYY-MM-DD'),
          anchor.endOf('year').format('YYYY-MM-DD'),
        ];
      default:
        return [
          anchor.startOf('month').format('YYYY-MM-DD'),
          anchor.endOf('month').format('YYYY-MM-DD'),
        ];
    }
  };

  // Highlights uses its own period + date independent from the table.
  const loadHighlights = async () => {
    try {
      const [start, end] = rangeFor(highlightsPeriod, highlightsDate);
      const response = await reportingAPI.getPayoutData(start, end);
      const payouts = response.success ? response.data?.payouts || [] : [];
      const t = payouts.reduce(
        (acc: any, item: any) => {
          const donationCount = item.donationCount || 0;
          const platformFee = item.platformFee ?? donationCount * 3;
          const ccFees = item.processingFees || 0;
          const total = item.totalDonations || 0;
          return {
            totalDonations: acc.totalDonations + total,
            totalCCFees: acc.totalCCFees + ccFees,
            totalPlatformFee: acc.totalPlatformFee + platformFee,
            totalPayoutAmount:
              acc.totalPayoutAmount +
              (item.payoutAmount ?? total - platformFee - ccFees),
            totalDonationCount: acc.totalDonationCount + donationCount,
          };
        },
        {
          totalDonations: 0,
          totalCCFees: 0,
          totalPlatformFee: 0,
          totalPayoutAmount: 0,
          totalDonationCount: 0,
        },
      );
      setHighlightsTotals(t);
    } catch (err) {
      console.error('loadHighlights failed:', err);
      setHighlightsTotals(null);
    }
  };

  // Load payout table data — driven by payoutsPeriod (Month, Quarter or Year).
  const loadPayoutData = async () => {
    setLoading(true);
    try {
      const [monthStart, monthEnd] = rangeFor(payoutsPeriod, selectedMonth);

      const response = await reportingAPI.getPayoutData(monthStart, monthEnd);

      // Backend response shape: {success, data: {payouts: [...], summary: {...}}}
      const payouts = response.success ? response.data?.payouts || [] : [];

      if (payouts.length > 0) {
        // Transform API data to PayoutData format.
        // Backend returns camelCase fields (see adminReporting.ts) — use them directly.
        const transformed: PayoutData[] = payouts.map((item: any) => {
          const totalDonations = item.totalDonations || 0;
          const donationCount = item.donationCount || 0;
          const ccProcessingFees = item.processingFees || 0;
          const platformFee = item.platformFee ?? donationCount * 3;
          // What the donor was charged toward Stripe, minus what Stripe took.
          // The app grosses up at 2.2% + $0.30 and rounds UP to the cent, so
          // this is normally a small positive figure that ends up in the
          // beneficiary's payout rather than anyone's pocket.
          const collectedForFees = Math.max(
            0,
            totalDonations - (item.monthlyDonations || 0) - (item.oneTimeGifts || 0),
          );
          const feeSurplus =
            Math.round((collectedForFees - ccProcessingFees) * 100) / 100;
          const payoutAmount = item.payoutAmount ?? totalDonations - platformFee - ccProcessingFees;
          const netAmount = item.netAmount ?? payoutAmount;

          // Stripe reconciliation isn't returned by backend yet — leave as pending.
          const stripeAmount = item.stripeAmount || 0;
          const reconciliationStatus: 'matched' | 'needs_review' | 'pending' = 'pending';

          return {
            key: item.beneficiaryId?.toString() || Math.random().toString(),
            beneficiaryId: item.beneficiaryId,
            beneficiaryName: item.beneficiaryName || 'Unknown',
            totalDonations,
            monthlyDonations: item.monthlyDonations || 0,
            oneTimeDonations: item.oneTimeGifts || 0,
            donationCount,
            ccProcessingFees,
            feeSurplus,
            netAmount,
            platformFee,
            payoutAmount,
            stripeAmount,
            reconciliationStatus,
            bankInfo: {
              hasBankInfo: Boolean(item.bankInfo?.accountNumber),
              bankName: item.bankInfo?.accountName,
              accountHolderName: item.bankInfo?.accountName,
              routingNumber: item.bankInfo?.routingNumber,
              accountNumber: item.bankInfo?.accountNumber || undefined,
              paymentMethod: item.bankInfo?.paymentMethod || 'check'
            },
            payoutStatus: item.payoutStatus || 'pending',
            payoutDate: item.payoutDate,
            notes: item.payoutNotes
          };
        });
        
        setPayoutData(transformed);
      } else {
        setPayoutData([]);
      }
    } catch (error: any) {
      console.error('Error loading payout data:', error);
      setPayoutData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayoutData();
  }, [selectedMonth, payoutsPeriod]);

  useEffect(() => {
    loadHighlights();
  }, [highlightsPeriod, highlightsDate]);

  // Stamp last_payment_date on any monthly_donations rows that never received
  // the Stripe webhook (so they show up in admin reporting under the month
  // they actually paid). Idempotent — safe to click again any time.
  const handleBackfillPaymentDates = async () => {
    setBackfillLoading(true);
    try {
      const response = await reportingAPI.backfillPaymentDates();
      if (response.success) {
        const txns = (response as any).transactionsUpserted ?? 0;
        message.success(
          `Backfill complete: ${(response as any).updated || 0} of ${(response as any).scanned || 0} subscriptions stamped from Stripe${txns ? `, ${txns} transactions upserted` : ''}.`
        );
        loadPayoutData();
      } else {
        message.error('Backfill failed. Check server logs.');
      }
    } catch (error: any) {
      console.error('Backfill error:', error);
      message.error(error.message || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  // Filter by needs review and bank info
  const displayedPayoutData = payoutData.filter((p) => {
    if (needsReviewFilter && p.reconciliationStatus !== 'needs_review') return false;
    if (bankInfoFilter === 'has' && !p.bankInfo.hasBankInfo) return false;
    if (bankInfoFilter === 'missing' && p.bankInfo.hasBankInfo) return false;
    return true;
  });

  // Calculate totals from displayed data
  const totals = displayedPayoutData.reduce((acc, item) => ({
    totalDonations: acc.totalDonations + item.totalDonations,
    totalMonthlyDonations: acc.totalMonthlyDonations + item.monthlyDonations,
    totalOneTimeDonations: acc.totalOneTimeDonations + item.oneTimeDonations,
    totalCCFees: acc.totalCCFees + item.ccProcessingFees,
    totalFeeSurplus: acc.totalFeeSurplus + (item.feeSurplus || 0),
    totalNetAmount: acc.totalNetAmount + item.netAmount,
    totalPlatformFee: acc.totalPlatformFee + item.platformFee,
    totalPayoutAmount: acc.totalPayoutAmount + item.payoutAmount,
    totalStripeAmount: acc.totalStripeAmount + item.stripeAmount,
    totalDonationCount: acc.totalDonationCount + item.donationCount
  }), {
    totalDonations: 0,
    totalMonthlyDonations: 0,
    totalOneTimeDonations: 0,
    totalCCFees: 0,
    totalFeeSurplus: 0,
    totalNetAmount: 0,
    totalPlatformFee: 0,
    totalPayoutAmount: 0,
    totalStripeAmount: 0,
    totalDonationCount: 0
  });

  // Handle bank info edit
  const handleEditBankInfo = (record: PayoutData) => {
    setSelectedBeneficiary(record);
    form.setFieldsValue({
      bankName: record.bankInfo.bankName || '',
      accountHolderName: record.bankInfo.accountHolderName || '',
      routingNumber: record.bankInfo.routingNumber || '',
      accountNumber: '', // Don't pre-fill for security
      paymentMethod: record.bankInfo.paymentMethod || 'direct_deposit'
    });
    setBankInfoModalVisible(true);
  };

  // Save bank info
  const handleSaveBankInfo = async () => {
    try {
      const values = await form.validateFields();
      
      if (!selectedBeneficiary) return;
      
      const response = await reportingAPI.updateBankInfo(selectedBeneficiary.beneficiaryId, {
        bank_name: values.bankName,
        account_holder_name: values.accountHolderName,
        routing_number: values.routingNumber,
        account_number: values.accountNumber,
        payment_method: values.paymentMethod,
        // Compatibility with backend expecting different keys
        accountName: values.accountHolderName,
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        paymentMethod: values.paymentMethod
      });
      
      if (response.success) {
        message.success('Bank information updated successfully');
        setBankInfoModalVisible(false);
        form.resetFields();
        loadPayoutData();
      } else {
        message.error(response.error || response.message || 'Failed to update bank information');
      }
    } catch (error: any) {
      console.error('Error saving bank info:', error);
      message.error(error?.message || 'Error saving bank information');
    }
  };

  // Handle payout status update
  const handleUpdatePayoutStatus = (record: PayoutData, newStatus: string) => {
    setSelectedBeneficiary(record);
    form.setFieldsValue({
      payoutStatus: newStatus,
      payoutDate: record.payoutDate ? dayjs(record.payoutDate) : dayjs(),
      notes: record.notes || ''
    });
    setPayoutStatusModalVisible(true);
  };

  // Save payout status
  const handleSavePayoutStatus = async () => {
    try {
      const values = await form.validateFields();
      
      if (!selectedBeneficiary) return;
      
      const response = await reportingAPI.updatePayoutStatus(selectedBeneficiary.beneficiaryId, {
        payout_status: values.payoutStatus,
        payout_date: values.payoutDate?.format('YYYY-MM-DD'),
        notes: values.notes
      });
      
      if (response.success) {
        message.success('Payout status updated successfully');
        setPayoutStatusModalVisible(false);
        form.resetFields();
        loadPayoutData();
      } else {
        message.error('Failed to update payout status');
      }
    } catch (error: any) {
      console.error('Error saving payout status:', error);
      message.error('Error saving payout status');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      'Beneficiary Name',
      'Total Donations',
      'Monthly Donations',
      'One-Time Donations',
      'Donation Count',
      'CC Processing Fees',
      'Net Amount',
      'Platform Fee ($3/donation)',
      'Payout to Beneficiary',
      'Stripe Amount',
      'Reconciliation Status',
      'Payment Method',
      'Payout Status',
      'Payout Date',
      'Notes'
    ];
    
    const rows = payoutData.map(item => [
      item.beneficiaryName,
      item.totalDonations.toFixed(2),
      item.monthlyDonations.toFixed(2),
      item.oneTimeDonations.toFixed(2),
      item.donationCount,
      item.ccProcessingFees.toFixed(2),
      item.netAmount.toFixed(2),
      item.platformFee.toFixed(2),
      item.payoutAmount.toFixed(2),
      item.stripeAmount.toFixed(2),
      item.reconciliationStatus,
      item.bankInfo.paymentMethod === 'direct_deposit' ? 'Direct Deposit' : 'Check',
      item.payoutStatus,
      item.payoutDate || '',
      item.notes || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payouts_${selectedMonth.format('YYYY-MM')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('CSV exported successfully');
  };

  // Table columns
  const columns = [
    {
      title: 'Beneficiary',
      dataIndex: 'beneficiaryName',
      key: 'beneficiaryName',
      fixed: 'left' as const,
      width: 200,
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Bank Info',
      dataIndex: ['bankInfo', 'hasBankInfo'],
      key: 'bankInfo',
      width: 130,
      align: 'center' as const,
      filters: [
        { text: 'Has Bank Info', value: 'has' },
        { text: 'No Bank Info', value: 'missing' }
      ],
      onFilter: (value: unknown, record: PayoutData) => {
        if (value === 'has') return record.bankInfo.hasBankInfo;
        if (value === 'missing') return !record.bankInfo.hasBankInfo;
        return true;
      },
      render: (hasBankInfo: boolean) => (
        hasBankInfo ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>Has Bank Info</Tag>
        ) : (
          <Tooltip title="Bank information missing - will need to write check">
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>No Bank Info</Tag>
          </Tooltip>
        )
      )
    },
    {
      title: (
        <Space size={4}>
          Total Collected
          <Tooltip
            title={
              'The full amount charged to donors, not the donation itself. ' +
              'A $15 gift is charged as $15.00 donation + $3.00 platform fee + ' +
              '$0.72 to cover Stripe, i.e. $18.72. Note the Dashboard uses ' +
              '"Total Donations" to mean only the donation portion.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'totalDonations',
      key: 'totalDonations',
      width: 130,
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong style={{ color: '#52c41a' }}>${amount.toFixed(2)}</Text>
      ),
      sorter: (a: PayoutData, b: PayoutData) => a.totalDonations - b.totalDonations
    },
    {
      title: 'Monthly',
      dataIndex: 'monthlyDonations',
      key: 'monthlyDonations',
      width: 110,
      align: 'right' as const,
      render: (amount: number) => `$${amount.toFixed(2)}`
    },
    {
      title: 'One-Time',
      dataIndex: 'oneTimeDonations',
      key: 'oneTimeDonations',
      width: 110,
      align: 'right' as const,
      render: (amount: number) => `$${amount.toFixed(2)}`
    },
    {
      title: 'Donations',
      dataIndex: 'donationCount',
      key: 'donationCount',
      width: 100,
      align: 'center' as const,
      render: (count: number) => <Tag>{count}</Tag>
    },
    {
      title: (
        <Space size={4}>
          Stripe Fee (actual)
          <Tooltip
            title={
              'What Stripe actually charged, read from Stripe rather than estimated. ' +
              'This is not the amount collected from the donor for fees — see Fee Surplus.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'ccProcessingFees',
      key: 'ccProcessingFees',
      width: 130,
      align: 'right' as const,
      render: (fees: number) => (
        <Text type="secondary">{fees > 0 ? `$${fees.toFixed(2)}` : '-'}</Text>
      ),
      className: 'table-header-small'
    },
    {
      title: (
        <Space size={4}>
          Fee Surplus
          <Tooltip
            title={
              'Collected for fees minus what Stripe actually took. The app grosses ' +
              "the donor's total up using Stripe's nonprofit rate (2.2% + $0.30) and " +
              'rounds UP to the cent, so the charity is never short. Stripe usually ' +
              'charges slightly less, and the difference goes to the beneficiary — ' +
              'which is why their payout can exceed the donation. Example: donor pays ' +
              '$0.72 toward fees, Stripe takes $0.66, surplus $0.06.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'feeSurplus',
      key: 'feeSurplus',
      width: 120,
      align: 'right' as const,
      render: (v: number) => (
        <Text type="secondary" style={{ color: v > 0 ? '#52c41a' : undefined }}>
          {v > 0 ? `+$${v.toFixed(2)}` : v < 0 ? `-$${Math.abs(v).toFixed(2)}` : '-'}
        </Text>
      ),
      className: 'table-header-small'
    },
    {
      title: 'Net Amount',
      dataIndex: 'netAmount',
      key: 'netAmount',
      width: 120,
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong>${amount.toFixed(2)}</Text>
      )
    },
    {
      title: 'Platform Fee ($3/donation)',
      dataIndex: 'platformFee',
      key: 'platformFee',
      width: 130,
      align: 'right' as const,
      render: (fee: number) => (
        <Text style={{ color: '#DB8633' }}>${fee.toFixed(2)}</Text>
      ),
      className: 'table-header-small'
    },
    {
      title: 'Payout to Beneficiary',
      dataIndex: 'payoutAmount',
      key: 'payoutAmount',
      width: 130,
      align: 'right' as const,
      render: (amount: number) => (
        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
          ${amount.toFixed(2)}
        </Text>
      ),
      sorter: (a: PayoutData, b: PayoutData) => a.payoutAmount - b.payoutAmount,
      className: 'table-header-small'
    },
    {
      title: 'Stripe Amount',
      dataIndex: 'stripeAmount',
      key: 'stripeAmount',
      width: 130,
      align: 'right' as const,
      render: (amount: number) => `$${amount.toFixed(2)}`
    },
    {
      title: 'Reconciliation',
      dataIndex: 'reconciliationStatus',
      key: 'reconciliationStatus',
      width: 140,
      align: 'center' as const,
      render: (status: string, record: PayoutData) => {
        const difference = Math.abs(record.stripeAmount - record.totalDonations);
        const color = status === 'matched' ? 'green' : status === 'needs_review' ? 'red' : 'orange';
        const icon = status === 'matched' ? <CheckCircleOutlined /> : 
                     status === 'needs_review' ? <CloseCircleOutlined /> : 
                     <ClockCircleOutlined />;
        
        return (
          <Tooltip title={`Difference: $${difference.toFixed(2)}`}>
            <Tag color={color} icon={icon}>
              {status === 'matched' ? 'Matched' : 
               status === 'needs_review' ? 'Review' : 'Pending'}
            </Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'Payment Method',
      dataIndex: 'bankInfo',
      key: 'paymentMethod',
      width: 140,
      render: (bankInfo: PayoutData['bankInfo']) => (
        <Tag color={bankInfo.paymentMethod === 'direct_deposit' ? 'blue' : 'orange'}>
          {bankInfo.paymentMethod === 'direct_deposit' ? 'Direct Deposit' : 'Check'}
        </Tag>
      )
    },
    {
      title: 'Payout Status',
      dataIndex: 'payoutStatus',
      key: 'payoutStatus',
      width: 130,
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
          pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Pending' },
          processing: { color: 'processing', icon: <ReloadOutlined spin />, text: 'Processing' },
          completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
          cancelled: { color: 'error', icon: <CloseCircleOutlined />, text: 'Cancelled' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right' as const,
      width: 200,
      render: (_: any, record: PayoutData) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditBankInfo(record)}
            size="small"
          >
            Bank Info
          </Button>
          <Dropdown
            menu={{
              items: [
                { key: 'pending', label: 'Mark as Pending', icon: <ClockCircleOutlined /> },
                { key: 'processing', label: 'Mark as Processing', icon: <ReloadOutlined /> },
                { key: 'completed', label: 'Mark as Completed', icon: <CheckCircleOutlined /> },
                { key: 'cancelled', label: 'Mark as Cancelled', icon: <CloseCircleOutlined /> }
              ],
              onClick: ({ key }) => handleUpdatePayoutStatus(record, key)
            }}
          >
            <Button type="link" size="small">
              Status <DownOutlined />
            </Button>
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <Layout className="reporting-layout">
      <AdminSidebar
        activeKey="reporting"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      {/* Main Content */}
      <Layout className="standard-main-content">
        <Header className="standard-header" style={{ background: colorBgContainer }}>
          <Title level={2} style={{ margin: 0 }}>Reporting & Payouts</Title>
        </Header>

        <Content style={{ margin: '24px', minHeight: 280, background: colorBgContainer, padding: 24, borderRadius: borderRadiusLG }}>
          <DashboardSection
            title="Reporting Highlights"
            subtitle="What's moving through the platform"
            icon={<TrophyOutlined />}
          >
          {/* Highlights period selector — M / Q / Y chips + a picker whose type
              adapts to the selected period. Drives the four cards below. */}
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                background: '#f5f5f5',
                borderRadius: 8,
                padding: 3,
                gap: 2,
              }}
            >
              {(['monthly', 'quarterly', 'yearly'] as const).map((p) => {
                const active = p === highlightsPeriod;
                const label =
                  p === 'monthly' ? 'Month' : p === 'quarterly' ? 'Quarter' : 'Year';
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setHighlightsPeriod(p)}
                    style={{
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#262626' : '#595959',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <DatePicker
              picker={
                highlightsPeriod === 'monthly'
                  ? 'month'
                  : highlightsPeriod === 'quarterly'
                    ? 'quarter'
                    : 'year'
              }
              value={highlightsDate}
              onChange={(d) => d && setHighlightsDate(d)}
              allowClear={false}
            />
          </div>
          {/* Summary Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Donations"
                  value={highlightsTotals?.totalDonations ?? 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#52c41a' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {highlightsTotals?.totalDonationCount ?? 0} donations
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Stripe Processing Fees"
                  value={highlightsTotals?.totalCCFees ?? 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#ff4d4f' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  What Stripe took from gross
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Platform Fee ($3/donation)"
                  value={highlightsTotals?.totalPlatformFee ?? 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#DB8633' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  THRIVE Initiative
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Payouts to Beneficiaries"
                  value={highlightsTotals?.totalPayoutAmount ?? 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  To Beneficiaries
                </Text>
              </Card>
            </Col>
          </Row>
          </DashboardSection>

          <DashboardSection
            title="Beneficiary Payouts"
            subtitle="Breakdown of what each charity is owed for the selected period"
            icon={<CalculatorOutlined />}
          >
          {/* Filters and Actions */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Space size={8} wrap>
                  {/* Month / Quarter chip selector — adapts the picker type
                      and the date range sent to /admin/reporting/payouts. */}
                  <div
                    style={{
                      display: 'inline-flex',
                      background: '#f5f5f5',
                      borderRadius: 8,
                      padding: 3,
                      gap: 2,
                    }}
                  >
                    {(['monthly', 'quarterly', 'yearly'] as const).map((p) => {
                      const active = p === payoutsPeriod;
                      const label =
                        p === 'monthly'
                          ? 'Month'
                          : p === 'quarterly'
                            ? 'Quarter'
                            : 'Year';
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPayoutsPeriod(p)}
                          style={{
                            border: 'none',
                            padding: '5px 12px',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            cursor: 'pointer',
                            background: active ? '#fff' : 'transparent',
                            color: active ? '#262626' : '#595959',
                            boxShadow: active
                              ? '0 1px 2px rgba(0,0,0,0.08)'
                              : 'none',
                            transition: 'all 0.15s',
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <DatePicker
                    picker={
                      payoutsPeriod === 'quarterly'
                        ? 'quarter'
                        : payoutsPeriod === 'yearly'
                          ? 'year'
                          : 'month'
                    }
                    value={selectedMonth}
                    onChange={(date) => date && setSelectedMonth(date)}
                    format={
                      payoutsPeriod === 'quarterly'
                        ? '[Q]Q YYYY'
                        : payoutsPeriod === 'yearly'
                          ? 'YYYY'
                          : 'MMMM YYYY'
                    }
                    style={{ width: 160 }}
                    allowClear={false}
                  />
                </Space>
              </Col>
              <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadPayoutData}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Tooltip title="Pull the latest paid invoice date from Stripe for any donations missing last_payment_date">
                    <Button
                      onClick={handleBackfillPaymentDates}
                      loading={backfillLoading}
                    >
                      Backfill from Stripe
                    </Button>
                  </Tooltip>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportCSV}
                    type="primary"
                    style={{ backgroundColor: '#DB8633', borderColor: '#DB8633' }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={() => window.print()}
                  >
                    Print
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Payout Table */}
          <Card
            title={
              <Space>
                <CalculatorOutlined />
                <Text strong>
                  Beneficiary Payouts -{' '}
                  {payoutsPeriod === 'quarterly'
                    ? selectedMonth.format('[Q]Q YYYY')
                    : payoutsPeriod === 'yearly'
                      ? selectedMonth.format('YYYY')
                      : selectedMonth.format('MMMM YYYY')}
                </Text>
              </Space>
            }
            extra={
              (() => {
                const reviewCount = payoutData.filter(p => p.reconciliationStatus === 'needs_review').length;
                return reviewCount > 0 ? (
                  <Button
                    type={needsReviewFilter ? 'primary' : 'default'}
                    icon={<ReconciliationOutlined />}
                    onClick={() => setNeedsReviewFilter(!needsReviewFilter)}
                    style={needsReviewFilter ? { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' } : { color: '#ff4d4f', borderColor: '#ff4d4f' }}
                  >
                    {reviewCount} Need Review
                  </Button>
                ) : (
                  <Button
                    icon={<ReconciliationOutlined />}
                    disabled
                  >
                    All Reconciled
                  </Button>
                );
              })()
            }
          >
            <Table
              columns={columns}
              dataSource={displayedPayoutData}
              loading={loading}
              scroll={{ x: 1800 }}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} beneficiaries`
              }}
              summary={() => (
                <Table.Summary fixed>
                  {/* One cell per column, in column order. The table has 15
                      columns; this row previously had 14 because Bank Info had
                      no cell, so every figure rendered one column to the left
                      and none of them lined up with its header — the first
                      total appeared under Bank Info. Counts:
                      12 single cells (0-11) + colSpan 4 (12) = 16. */}
                  <Table.Summary.Row>
                    {/* 0 — Beneficiary */}
                    <Table.Summary.Cell index={0}>
                      <Text strong>Totals:</Text>
                    </Table.Summary.Cell>
                    {/* 1 — Bank Info: nothing to total, but the cell must exist */}
                    <Table.Summary.Cell index={1} />
                    {/* 2 — Total Donations */}
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong style={{ color: '#52c41a' }}>
                        ${totals.totalDonations.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    {/* 3 — Monthly */}
                    <Table.Summary.Cell index={3} align="right">
                      ${totals.totalMonthlyDonations.toFixed(2)}
                    </Table.Summary.Cell>
                    {/* 4 — One-Time */}
                    <Table.Summary.Cell index={4} align="right">
                      ${totals.totalOneTimeDonations.toFixed(2)}
                    </Table.Summary.Cell>
                    {/* 5 — Donations (count) */}
                    <Table.Summary.Cell index={5} align="center">
                      <Tag>{totals.totalDonationCount}</Tag>
                    </Table.Summary.Cell>
                    {/* 6 — Stripe Fee (actual) */}
                    <Table.Summary.Cell index={6} align="right">
                      ${totals.totalCCFees.toFixed(2)}
                    </Table.Summary.Cell>
                    {/* 7 — Fee Surplus */}
                    <Table.Summary.Cell index={7} align="right">
                      <Text style={{ color: totals.totalFeeSurplus > 0 ? '#52c41a' : undefined }}>
                        {totals.totalFeeSurplus > 0
                          ? `+$${totals.totalFeeSurplus.toFixed(2)}`
                          : `$${totals.totalFeeSurplus.toFixed(2)}`}
                      </Text>
                    </Table.Summary.Cell>
                    {/* 8 — Net Amount */}
                    <Table.Summary.Cell index={8} align="right">
                      <Text strong>${totals.totalNetAmount.toFixed(2)}</Text>
                    </Table.Summary.Cell>
                    {/* 9 — Platform Fee */}
                    <Table.Summary.Cell index={9} align="right">
                      <Text style={{ color: '#DB8633' }}>
                        ${totals.totalPlatformFee.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    {/* 10 — Payout to Beneficiary */}
                    <Table.Summary.Cell index={10} align="right">
                      <Text strong style={{ color: '#1890ff', fontSize: '18px' }}>
                        ${totals.totalPayoutAmount.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    {/* 11 — Stripe Amount */}
                    <Table.Summary.Cell index={11} align="right">
                      ${totals.totalStripeAmount.toFixed(2)}
                    </Table.Summary.Cell>
                    {/* 12 — Reconciliation, Payment Method, Payout Status, Actions */}
                    <Table.Summary.Cell index={12} colSpan={4} />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Card>
          </DashboardSection>
        </Content>
      </Layout>

      {/* Bank Info Modal */}
      <Modal
        title={`Bank Information - ${selectedBeneficiary?.beneficiaryName}`}
        open={bankInfoModalVisible}
        onOk={handleSaveBankInfo}
        onCancel={() => {
          setBankInfoModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="paymentMethod"
            label="Payment Method"
            rules={[{ required: true, message: 'Please select payment method' }]}
          >
            <Select>
              <Option value="direct_deposit">Direct Deposit (Preferred)</Option>
              <Option value="check">Check</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="bankName"
            label="Bank Name"
            rules={[{ required: true, message: 'Please enter bank name' }]}
          >
            <Input placeholder="Enter bank name" />
          </Form.Item>
          
          <Form.Item
            name="accountHolderName"
            label="Account Holder Name"
            rules={[{ required: true, message: 'Please enter account holder name' }]}
          >
            <Input placeholder="Enter account holder name" />
          </Form.Item>
          
          <Form.Item
            name="routingNumber"
            label="Routing Number"
            rules={[
              { required: true, message: 'Please enter routing number' },
              { pattern: /^\d{9}$/, message: 'Routing number must be 9 digits' }
            ]}
          >
            <Input placeholder="123456789" maxLength={9} />
          </Form.Item>
          
          <Form.Item
            name="accountNumber"
            label="Account Number"
            rules={[
              { required: true, message: 'Please enter account number' },
              { min: 4, message: 'Account number must be at least 4 digits' }
            ]}
          >
            <Input.Password placeholder="Enter account number" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Payout Status Modal */}
      <Modal
        title={`Update Payout Status - ${selectedBeneficiary?.beneficiaryName}`}
        open={payoutStatusModalVisible}
        onOk={handleSavePayoutStatus}
        onCancel={() => {
          setPayoutStatusModalVisible(false);
          form.resetFields();
        }}
        width={500}
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="payoutStatus"
            label="Payout Status"
            rules={[{ required: true, message: 'Please select payout status' }]}
          >
            <Select>
              <Option value="pending">Pending</Option>
              <Option value="processing">Processing</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="payoutDate"
            label="Payout Date"
            rules={[{ required: true, message: 'Please select payout date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={4} placeholder="Add any notes about this payout..." />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Reporting;

